import { lazy, type ComponentType } from "react";

/**
 * lazy(), but it survives a deploy.
 *
 * Chunks are content-hashed, so a deploy replaces every filename. A visitor
 * who had the site open before it went out is running the previous index with
 * the previous chunk names baked in: the moment they need a chunk they have
 * not loaded yet -- a route, or the sign-in popup -- the browser asks for a
 * file that no longer exists, gets a 404, and the error boundary replaces the
 * page with an error screen. On a storefront that is somebody who was about
 * to buy.
 *
 * A failed import of a chunk that used to exist means the page is stale, and
 * the fix is a reload -- the fresh index names the new chunks. The reload is
 * recorded in sessionStorage first so a genuinely missing file, or one
 * blocked by an extension, cannot put the tab in a refresh loop: the second
 * failure is allowed to throw and the error boundary handles it as before.
 */
const RELOADED = "veyra:chunk-reload";

// Components of any props; the constraint is only that it is a component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err: unknown) => {
      let alreadyTried = true;
      try {
        alreadyTried = sessionStorage.getItem(RELOADED) === "1";
        if (!alreadyTried) sessionStorage.setItem(RELOADED, "1");
      } catch {
        /* private mode, or storage blocked: treat as already tried and throw */
      }
      if (!alreadyTried) {
        window.location.reload();
        /* Never settles; the reload replaces the document. Resolving with
           anything here would flash the wrong thing on the way out. */
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

/* A load that succeeds means whatever chunks this session needs are being
   served, so the one-shot reload is armed again for the next deploy. */
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    try {
      sessionStorage.removeItem(RELOADED);
    } catch {
      /* nothing to clear */
    }
  });
}
